<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-snackbar
          v-model="snackbar.visible"
          :color="snackbar.color"
          :timeout="snackbar.timeout"
          :top="snackbar.position === 'top'"
          max-width="400"
        >
          <v-layout align-center pr-4>
            <v-icon class="pr-3" dark large>{{ snackbar.icon }}</v-icon>
            <v-layout column>
              <div><strong>{{ snackbar.title }}</strong></div>
              <div>{{ snackbar.text }}</div>
            </v-layout>
          </v-layout>
          <template v-slot:action="{ attrs }">
            <v-btn icon v-bind="attrs" @click="snackbar.visible = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </v-col>
    </v-row>

    <v-card class="mx-auto pa-4" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-avatar color="primary" size="48" class="elevation-2 mr-4">
          <v-icon dark size="28">mdi-trophy</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-bold primary--text">
            {{ reportTitle || 'Score Recap' }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            Rekap score performance KPI individu
          </div>
        </div>
      </v-card-title>

      <v-tabs v-model="activeTab" background-color="#125a97" dark>
        <v-tab>
          <v-icon left>mdi-table-star</v-icon>
          Score Performance
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-card outlined class="mt-2">
                <v-card-title>
                  <v-icon color="primary" class="mr-2">mdi-chart-line</v-icon>
                  {{ entityName || 'Data Score' }}
                  <v-spacer></v-spacer>
                  <v-btn
                    color="success"
                    dark
                    small
                    :loading="exporting"
                    @click="exportExcel"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-microsoft-excel</v-icon>
                    Export Excel
                  </v-btn>
                </v-card-title>

                <div v-if="loading" class="text-center py-10">
                  <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                  <div class="mt-3 grey--text">Memuat data...</div>
                </div>

                <div v-else-if="periodLabels.length === 0" class="text-center py-10">
                  <v-icon large color="grey lighten-1">mdi-table-off</v-icon>
                  <div class="mt-3 grey--text">Tidak ada data score.</div>
                </div>

                <div v-else class="table-wrapper">
                  <table class="score-table">
                    <thead>
                      <tr>
                        <th class="col-no">No</th>
                        <th class="col-nama">Nama</th>
                        <th class="col-jabatan">Jabatan</th>
                        <th
                          v-for="(label, idx) in periodLabels"
                          :key="'h-' + idx"
                          class="col-month"
                        >
                          {{ label }}
                        </th>
                        <th class="col-ytd col-ytd-sem">Score YTD<br>Semester 1</th>
                        <th class="col-ytd col-ytd-sem">Score YTD<br>Semester 2</th>
                        <th class="col-ytd">Score YTD</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, rowIdx) in scoreData"
                        :key="'row-' + rowIdx"
                        :class="rowIdx % 2 === 1 ? 'row-alt' : ''"
                      >
                        <td class="col-no">{{ rowIdx + 1 }}</td>
                        <td class="col-nama">{{ item.nama || 'N/A' }}</td>
                        <td class="col-jabatan">{{ item.jabatan || '-' }}</td>
                        <td
                          v-for="(month, mIdx) in periodMonths"
                          :key="'cell-' + rowIdx + '-' + mIdx"
                          class="col-month"
                          :class="{ 'cell-grey': getScore(item.scores, month) == null }"
                        >
                          {{ formatScore(getScore(item.scores, month)) }}
                        </td>
                        <td class="col-ytd col-ytd-sem" :class="{ 'cell-grey': item.ytd_semester_1 == null }">{{ formatScore(item.ytd_semester_1) }}</td>
                        <td class="col-ytd col-ytd-sem" :class="{ 'cell-grey': item.ytd_semester_2 == null }">{{ formatScore(item.ytd_semester_2) }}</td>
                        <td class="col-ytd" :class="{ 'cell-grey': item.ytd == null }">{{ formatScore(item.ytd) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </v-card>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
export default {
	data() {
		return {
			activeTab: 0,
			loading: false,
			exporting: false,
			reportTitle: "",
			entityName: "",
			periodMonths: [],
			periodLabels: [],
			scoreData: [],
			snackbar: {
				color: null,
				icon: null,
				mode: null,
				position: "top",
				text: null,
				timeout: 5000,
				title: null,
				visible: false,
			},
		};
	},
	computed: {
		entity() {
			return this.$store.state.entity;
		},
	},
	watch: {
		"entity.id": {
			async handler(newVal, oldVal) {
				setTimeout(() => {
					if (newVal && newVal !== oldVal) {
						this.$nextTick(() => {
							this.fetchScores();
						});
					}
				}, 200);
			},
			immediate: false,
		},
	},
	mounted() {
		this.waitForTokenAndLoad();
	},
	methods: {
		async waitForTokenAndLoad() {
			let retries = 5;
			while (!this.$store.getters.isTokenUser && retries-- > 0) {
				await new Promise((resolve) => setTimeout(resolve, 300));
			}
			if (!this.$store.getters.isTokenUser) return;
			this.fetchScores();
		},

		async fetchScores() {
			this.loading = true;
			this.$store.dispatch("setOverlay", true);

			try {
				const params = {};
				if (this.entity?.id) {
					params.entity_id = this.entity.id;
				}

				const res = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/monitoring/score-performance`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
						},
						params,
					}
				);

				const data = res.data;

				this.reportTitle = data.report_title || "";
				this.entityName = data.entity_name || "";
				this.periodMonths = data.period_months || [];
				this.periodLabels = data.period_labels || [];
				this.scoreData = data.data || [];
			} catch (err) {
				console.error(
					"Gagal memuat data score:",
					err?.response?.data?.message || err?.message || err
				);
				this.snackbar = {
					color: "error",
					icon: "mdi-alert-circle-outline",
					mode: "snackbar",
					position: "top",
					text:
						err?.response?.data?.message ||
						"Gagal memuat data score. Silakan coba lagi.",
					timeout: 5000,
					title: null,
					visible: true,
				};
			} finally {
				this.loading = false;
				this.$store.dispatch("setOverlay", false);
			}
		},

		async exportExcel() {
			this.exporting = true;
			try {
				const params = {};
				if (this.entity?.id) {
					params.entity_id = this.entity.id;
				}

				const res = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/monitoring/score-performance/excel`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
						},
						params,
						responseType: "blob",
					}
				);

				const blob = new Blob([res.data], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});
				const url = globalThis.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute(
					"download",
					`Score_Recap_${this.entityName || "Report"}.xlsx`
				);
				document.body.appendChild(link);
				link.click();
				link.remove();
				globalThis.URL.revokeObjectURL(url);

				this.snackbar = {
					color: "success",
					icon: "mdi-check-circle",
					mode: "snackbar",
					position: "top",
					text: "Export Excel berhasil!",
					timeout: 3000,
					title: null,
					visible: true,
				};
			} catch (err) {
				console.error(
					"Gagal export excel:",
					err?.response?.data?.message || err?.message || err
				);
				this.snackbar = {
					color: "error",
					icon: "mdi-alert-circle-outline",
					mode: "snackbar",
					position: "top",
					text:
						err?.response?.data?.message ||
						"Gagal export excel. Silakan coba lagi.",
					timeout: 5000,
					title: null,
					visible: true,
				};
			} finally {
				this.exporting = false;
			}
		},

		getScore(monthlyScores, month) {
			if (!monthlyScores || monthlyScores[month] == null) return null;
			return monthlyScores[month];
		},

		formatScore(value) {
			if (value == null) return "-";
			if (value === 0) return "0";
			return Number(value)
				.toFixed(2)
				.replace(/\.?0+$/, "");
		},
	},
};
</script>

<style scoped>
.table-wrapper {
	overflow-x: auto;
	padding: 0;
}

.score-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
	min-width: 900px;
}

.score-table thead th {
	background-color: #2d4f6c;
	color: #fff;
	padding: 8px 10px;
	text-align: center;
	font-weight: 600;
	white-space: nowrap;
	border: 1px solid #ddd;
	position: sticky;
	top: 0;
	z-index: 1;
}

.score-table tbody td {
	padding: 6px 10px;
	border: 1px solid #ddd;
	text-align: center;
	white-space: nowrap;
}

.score-table .col-no {
	width: 45px;
	min-width: 45px;
	text-align: center;
}

.score-table .col-nama {
	text-align: left;
	min-width: 180px;
	max-width: 250px;
	white-space: normal;
	word-break: break-word;
}

.score-table .col-jabatan {
	text-align: left;
	min-width: 160px;
	max-width: 250px;
	white-space: normal;
	word-break: break-word;
}

.score-table .col-month {
	min-width: 65px;
}

.score-table .col-ytd {
	min-width: 85px;
	background-color: #bbdefb;
	color: #0d47a1;
	font-weight: 700;
}

.score-table thead .col-ytd {
	background-color: #1565c0;
	color: #fff;
}

.score-table .col-ytd-sem {
	min-width: 60px;
	background-color: #e1f0fb;
	color: #0d47a1;
	font-weight: 600;
}

.score-table thead .col-ytd-sem {
	background-color: #4a90c4;
	color: #fff;
	white-space: normal;
	line-height: 1.3;
}

.score-table tbody tr:hover {
	background-color: #e3f2fd !important;
}

.row-alt {
	background-color: #f5f9ff;
}

.cell-grey {
	background-color: #e0e0e0 !important;
	color: #757575;
}
</style>
